import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { CloseIcon } from "@/assets/icons";
import Dropdown from "@/components/ui/dropdown";
import FormField from "@/components/ui/formField";
import {
  EditGroupFormData,
  GetGroupByIdDetail,
  GroupStatus,
  UserGroupPermissionPayload
} from "@/types/groups";

import { getPermissionOptions, GROUP_STATUS_OPTIONS } from "../data";
import FormFooterActions from "./FormFooterAction";
import PermissionTable from "./PermissionTable";

const TEST_ID_PREFIX = "edit-group-panel";

type EditGroupPanelProps = {
  groupDetailById: GetGroupByIdDetail;
  onClose?: () => void; // Optional onClose function for future use
  onSubmit: (data: EditGroupFormData) => void; // Optional onSubmit function for future use
  isSubmitting?: boolean; // Flag to indicate if the form is submitting
};

export const EditGroupPanel = ({
  groupDetailById,
  onClose,
  onSubmit,
  isSubmitting = false
}: EditGroupPanelProps) => {
  // Need to check whether the group name is changed or not
  // If the group name is not changed, we will not send the name in the payload
  const initialName = groupDetailById.name;
  const getUserGroupPermission = groupDetailById?.features?.map((item) => ({
    name: item.name,
    feature_id: item.id,
    selectedPermission: getPermissionOptions(item.name).find(
      (opt) => opt.value === item.permission
    )
  })) as UserGroupPermissionPayload[];
  const editGroupFormProviderProps = useForm<EditGroupFormData>({
    mode: "onChange",
    defaultValues: {
      id: groupDetailById.id,
      name: groupDetailById.name,
      description: groupDetailById.description,
      status: groupDetailById.status,
      group_permission: getUserGroupPermission
    }
  });

  const { register, formState, handleSubmit, setValue } =
    editGroupFormProviderProps;
  const [selectedStatus, setSelectedStatus] = useState(
    GROUP_STATUS_OPTIONS.find(
      ({ value }) => value === groupDetailById.status
    ) ?? GROUP_STATUS_OPTIONS[0]
  );

  useEffect(() => {
    // To show a confirmation dialog when the user tries to leave the page
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  return (
    <FormProvider {...editGroupFormProviderProps}>
      <form
        className="flex flex-1 flex-col"
        onSubmit={(e) => {
          handleSubmit((payload: EditGroupFormData) => {
            onSubmit?.({
              ...payload,
              // Need to check whether the group name is changed or not
              // If the group name is not changed, we will not send the name in the payload
              name: payload.name === initialName ? null : payload.name
            });
          })(e);
        }}
      >
        <div className="sticky top-0 z-10 p-6 bg-white">
          <div className="flex flex-row justify-between items-center">
            <p className="text-txtHeading font-sans font-bold text-[20px]">
              Edit Group
            </p>
            <div className="flex gap-3">
              <button
                type="button" // Default button type is submit. Thus, need to set type="button" to prevent form submission
                aria-label="Close Edit Group Panel"
                onClick={onClose}
                className="bg-transparent border-0 p-0"
                data-testid={`${TEST_ID_PREFIX}-close-button`}
              >
                <CloseIcon className="fill-dim-gray" />
              </button>
            </div>
          </div>
        </div>
        <div className="flex flex-1 flex-col px-6 gap-3 overflow-auto">
          <FormField
            className="mt-2 h-10"
            register={register}
            placeHolder="Group name"
            type="text"
            name="name"
            rules={{ required: true }}
            autoCleanError={false}
            hasSpacingOnFocus={false}
            disabled={isSubmitting}
          />
          <FormField
            register={register}
            placeHolder="Description"
            name="description"
            rules={{ required: true }}
            autoCleanError={false}
            hasSpacingOnFocus={false}
            disabled={isSubmitting}
            isAreaInput
          />

          <Dropdown
            name="groupStatus"
            className="mt-1"
            labelBorder="Status"
            data={GROUP_STATUS_OPTIONS}
            defaultValue={selectedStatus}
            value={selectedStatus}
            disabled={isSubmitting}
            onSelect={(data) => {
              setValue("status", data?.value as GroupStatus);
              setSelectedStatus(data as { label: string; value: GroupStatus });
            }}
          />
          <PermissionTable
            isSubmitting={isSubmitting}
            permissions={getUserGroupPermission}
          />
        </div>
        <FormFooterActions
          primaryLabel="Update"
          primaryDisabled={!formState.isValid}
          isSubmitting={isSubmitting}
          secondaryLabel="Cancel"
          onSecondaryButtonClicked={onClose}
          testId={TEST_ID_PREFIX}
        />
      </form>
    </FormProvider>
  );
};

export default EditGroupPanel;
